import type { DirectoryTeam, Team } from '@nexo/types';
import { Avatar, Button, EmptyState, Input, Spinner, useToast } from '@nexo/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Compass, Search, Star, Users } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/forms';
import { formatNumber } from '../../lib/format';
import { keys, useFeatures } from '../../lib/queries';
import { OfficialBadge } from './OfficialBadge';

/** «Explorar servidores»: public teams anyone can join (turned on from /admin). */
export function ExplorePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const features = useFeatures();
  const [query, setQuery] = useState('');
  const q = useDeferredValue(query.trim());
  const enabled = features.data?.publicDirectory === true;
  const list = useQuery({
    queryKey: keys.directory(q),
    queryFn: () => api.directory.list(q || undefined),
    enabled,
    placeholderData: keepPreviousData,
  });

  if (features.data && !enabled) return <Navigate to="/app/home" replace />;

  return (
    <div className="min-h-full overflow-y-auto bg-canvas">
      <div className="mx-auto max-w-4xl px-5 py-8">
        <button
          type="button"
          onClick={() => void navigate(-1)}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted hover:text-text"
        >
          <ArrowLeft className="size-4" /> {t('Volver')}
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
              <Compass className="size-7 text-accent" /> {t('Explorar servidores')}
            </h1>
            <p className="mt-1.5 text-muted">
              {t('Comunidades públicas en Nexo: entra, pregunta y conoce a otros equipos.')}
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-subtle" />
            <Input
              aria-label={t('Buscar servidores')}
              placeholder={t('Buscar servidores')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {!list.data ? (
          <div className="flex justify-center py-20 text-muted">
            <Spinner className="size-6" />
          </div>
        ) : list.data.length === 0 ? (
          <EmptyState
            className="mt-10 rounded-2xl border border-border bg-surface"
            icon={<Compass className="size-8" />}
            title={q ? t('Ningún servidor coincide') : t('Todavía no hay servidores públicos')}
            description={t(
              'Los propietarios pueden hacer público su equipo desde los ajustes del equipo.',
            )}
          />
        ) : (
          <ul className="mt-8 grid gap-4 sm:grid-cols-2">
            {list.data.map((team) => (
              <DirectoryCard key={team.id} team={team} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DirectoryCard({ team }: { team: DirectoryTeam }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const open = () => void navigate(`/app/t/${team.id}`);

  const join = useMutation({
    mutationFn: () => api.directory.join(team.id),
    onSuccess: (joined) => {
      queryClient.setQueryData<Team[]>(keys.teams, (list) =>
        list && !list.some((other) => other.id === joined.id) ? [...list, joined] : list,
      );
      void queryClient.invalidateQueries({ queryKey: ['directory'] });
      open();
    },
    onError: (err) =>
      toast({ title: t('No se pudo entrar'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <li className="flex flex-col rounded-2xl border border-border bg-surface p-5 shadow-panel">
      <div className="flex items-start gap-3">
        <Avatar id={team.id} name={team.name} shape="square" className="size-12 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 font-semibold">
            <span className="truncate">{team.name}</span>
            {team.official && <OfficialBadge />}
            {team.featured && !team.official && (
              <Star
                className="size-4 shrink-0 fill-warning text-warning"
                aria-label={t('Destacado')}
              />
            )}
          </p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-muted">
            <Users className="size-3.5" />
            {team.memberCount === 1
              ? t('1 miembro')
              : t('{{count}} miembros', { count: formatNumber(team.memberCount) })}
          </p>
        </div>
      </div>
      <p className="mt-3 line-clamp-3 flex-1 text-sm text-muted">
        {team.description || t('Sin descripción')}
      </p>
      <div className="mt-4 flex justify-end">
        {team.joined ? (
          <Button variant="secondary" onClick={open}>
            {t('Abrir')}
          </Button>
        ) : (
          <Button loading={join.isPending} onClick={() => join.mutate()}>
            {t('Unirme')}
          </Button>
        )}
      </div>
    </li>
  );
}
